import { useState, useRef, useCallback, useEffect } from 'react';

interface UseDraggableOptions {
  initialPosition?: { x: number; y: number };
  disabled?: boolean;
}

export const useDraggable = ({
  initialPosition = { x: 0, y: 0 },
  disabled = false,
}: UseDraggableOptions = {}) => {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);

  const elementRef = useRef<HTMLDivElement>(null);
  const dragStartOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      // Ensure the drag handle was clicked and not a button inside it
      if ((e.target as HTMLElement).closest('button')) return;

      e.preventDefault();
      setIsDragging(true);
      dragStartOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [position.x, position.y],
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!elementRef.current) return;
    const newX = e.clientX - dragStartOffset.current.x;
    const newY = e.clientY - dragStartOffset.current.y;
    // We use transform for performance during the drag, avoiding re-renders
    elementRef.current.style.transform = `translate(${newX}px, ${newY}px)`;
  }, []);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    // When dragging stops, update the state to the final position
    const finalX = e.clientX - dragStartOffset.current.x;
    const finalY = e.clientY - dragStartOffset.current.y;
    setPosition({ x: finalX, y: finalY });
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp, { once: true });
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (elementRef.current && !disabled) {
      elementRef.current.style.transform = `translate(${position.x}px, ${position.y}px)`;
    } else if (elementRef.current && disabled) {
      elementRef.current.style.transform = '';
    }
  }, [position]);

  return {
    elementRef,
    handleMouseDown,
    isDragging,
    setPosition,
  };
};
